import { Link } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import { PROFILE_LABELS } from '@/frontend/pages/Dashboard/DigitalWorkers/constants'
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { connectedChannels } from './workerChannels'

interface Props {
  worker: DigitalWorkerDetail
  compact?: boolean
}

type FichaStatus = 'online' | 'creating' | 'failed'

// Pill de status do protótipo ("● Ativa"): semânticas ok/warn/err dos tokens.
const STATUS_CHIPS: Record<FichaStatus, { label: string; dot: string; chip: string; pulse: boolean }> = {
  online: { label: 'Ativa', dot: 'bg-success', chip: 'bg-success/12 text-success', pulse: false },
  creating: { label: 'Em criação', dot: 'bg-warning', chip: 'bg-warning/13 text-warning dark:bg-warning/15', pulse: true },
  failed: { label: 'Falha', dot: 'bg-destructive', chip: 'bg-destructive/12 text-destructive', pulse: false },
}

// Mirrors the status mapping the landing used with WorkerProfileCard.
function fichaStatus(worker: DigitalWorkerDetail): FichaStatus | null {
  if (worker.provisioning_status === 'ready') return 'online'
  if (worker.provisioning_status === 'failed' || worker.provisioning_stale) return 'failed'
  if (worker.provisioning_status === 'pending' || worker.provisioning_status === 'provisioning') return 'creating'
  return null
}

/**
 * Ficha de funcionário (SZ-1598, C.3): compact identity header of the worker
 * landing — avatar 64px, nome, cargo, canais, contratação, gestor e status.
 * The section pages keep the WorkerProfileCard banner; this header composes
 * over the same data the Show page already loads.
 */
export default function FichaHeader({ worker, compact = false }: Props) {
  const status = fichaStatus(worker)
  const chip = status ? STATUS_CHIPS[status] : null
  const displayName = worker.name.trim() || 'Seu colaborador digital'
  const cargo = worker.job_title || PROFILE_LABELS[worker.fleet_profile] || 'Cargo a definir'
  const hiredAt = new Date(worker.created_at).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  const gestor = worker.contact_name?.trim()
  const channels = connectedChannels(worker)

  return (
    // Header ficha-de-funcionário do protótipo: sem card em volta — avatar 56
    // com dot de status, nome 21/650 + pill "● Ativa", cargo · canais em
    // média e contratação/gestor 12 baixa. É só identidade: as ações do agente
    // (Falar/Pausar) saíram — o Chat é uma aba do portal e a sidebar tem a ação.
    <section
      data-testid="worker-profile-card"
      data-compact={compact || undefined}
      className={cn('pt-1', compact && 'pt-0')}
      style={agentHueStyle(worker.id)}
    >
      <div className={cn('flex flex-col gap-4 sm:flex-row sm:items-center', compact && 'flex-row items-center gap-2.5')}>
        <div className="relative shrink-0">
          <div className={cn(
            'overflow-hidden rounded-full border border-border bg-secondary',
            compact ? 'size-8' : 'h-14 w-14',
          )}>
            <img
              src={worker.avatar_url ?? '/images/avatar-silhouette.png'}
              alt={`Avatar de ${displayName}`}
              className="h-full w-full object-cover"
            />
          </div>
          {chip && (
            <span
              aria-hidden
              className={cn(
                'absolute -bottom-px -right-px h-[13px] w-[13px] rounded-full border-[2.5px] border-background',
                compact && 'h-2.5 w-2.5 border-2',
                chip.dot,
                chip.pulse && 'animate-live-pulse',
              )}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className={cn('flex flex-wrap items-center gap-2.5', compact && 'gap-2')}>
            <h2 className={cn(
              'truncate font-semibold leading-tight tracking-[-0.015em] text-foreground',
              compact ? 'text-[13.5px]' : 'text-[19px]',
            )}>
              {displayName}
            </h2>
            {chip && (
              <span
                data-testid="worker-profile-status-badge"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-[9px] py-0.5 text-[11px] font-semibold',
                  chip.chip,
                  compact && 'px-1.5 py-px text-[10px]',
                )}
              >
                <span className={cn('inline-flex h-1.5 w-1.5 rounded-full', chip.dot, chip.pulse && 'animate-live-pulse')} />
                {chip.label}
              </span>
            )}
          </div>
          <p className={cn('mt-1 truncate text-[13px] text-muted-foreground', compact && 'mt-0.5 text-[11px]')}>
            {cargo}
            {channels.length > 0 ? (
              ` · ${channels.map((channel) => channel.label).join(', ')}`
            ) : (
              <>
                {' · '}
                <Link
                  href={`/dashboard/digital_workers/${worker.id}/comunicacao`}
                  className="font-medium text-agent transition-opacity duration-150 hover:opacity-80"
                >
                  Conectar canais <span aria-hidden="true">→</span>
                </Link>
              </>
            )}
          </p>
          <p className={cn('mt-0.5 truncate text-xs text-faint-foreground', compact && 'hidden')}>
            Contratada em {hiredAt}
            {gestor && ` · Gestor: ${gestor}`}
          </p>
        </div>
      </div>
    </section>
  )
}
